import { Context, Next } from 'hono';
import { edgeCustomHashing, CustomSignedPayload, aghoraBitwiseHash, CustomHashFunction } from './custom-hash.ts';

export * from './custom-hash.ts';
export * from './tmd-hash-v12.ts';
export * from './automata-v10.ts';

export interface BetterAuthSessionOptions {
  cookieDomain: string; // e.g., '.bhutadamarasena.com'
  sessionTtlSeconds: number;
}

export const DEFAULT_AUTH_CONFIG: BetterAuthSessionOptions = {
  cookieDomain: '.bhutadamarasena.com',
  sessionTtlSeconds: 86400 * 30 // 30 Days
};

export interface AuthSession {
  userId: string;
  email: string;
  activeAppSubscriptions: string[]; // e.g. ['hora', 'hawan', 'all']
  tier: 'free' | 'pro' | 'sovereign' | 'custom';
}

export class SovereignAuth {
  /**
   * Generates a wildcard cookie header string scoped under bhutadamarasena.com
   */
  public static createSessionCookie(token: string, domain: string = DEFAULT_AUTH_CONFIG.cookieDomain): string {
    return `sovereign_session=${token}; Domain=${domain}; Path=/; Secure; HttpOnly; SameSite=Lax; Max-Age=${DEFAULT_AUTH_CONFIG.sessionTtlSeconds}`;
  }

  /**
   * Parses the session token from incoming HTTP Request headers.
   */
  public static parseSessionToken(req: Request): string | null {
    const cookieHeader = req.headers.get('Cookie');
    if (!cookieHeader) return null;

    const cookies = cookieHeader.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      if (key && value) acc[key] = decodeURIComponent(value);
      return acc;
    }, {} as Record<string, string>);

    return cookies['sovereign_session'] || null;
  }

  /**
   * Verifies if session data grants access to the target app ID.
   */
  public static hasAppAccess(session: AuthSession, targetAppId: string): boolean {
    if (!session || !session.activeAppSubscriptions) return false;
    const subs = session.activeAppSubscriptions.map(s => s.toLowerCase());
    return subs.includes('all') || subs.includes(targetAppId.toLowerCase());
  }
}

/**
 * HONO EDGE MIDDLEWARE: Enforces App-Scoped Access Control on Edge Workers
 */
export function requireAppAccess(requiredAppId: string) {
  return async (c: Context, next: Next) => {
    const sessionToken = SovereignAuth.parseSessionToken(c.req.raw);

    // Development / Master Hub preview bypass header check (with timestamp nonce replay protection)
    const hubProKey = c.req.header('X-Hub-Pro-Key') || '';
    let isMasterHubOverride = hubProKey === 'sovereign_hub_bypass';
    if (!isMasterHubOverride && hubProKey.startsWith('sovereign_hub_bypass_')) {
      const ts = parseInt(hubProKey.replace('sovereign_hub_bypass_', ''), 10);
      if (!isNaN(ts) && Math.abs(Date.now() - ts) < 300000) { // 300 seconds window
        isMasterHubOverride = true;
      }
    }

    if (isMasterHubOverride) {
      return await next();
    }

    const devUserId = c.req.header('X-Dev-User-Id');

    if (!sessionToken && !devUserId) {
      return c.json({
        error: 'UNAUTHORIZED_ECOSYSTEM_ACCESS',
        message: `Active subscription required for app: [${requiredAppId}]. Upgrade via Licensing & Tiers.`,
        requiredAppId,
        purchaseUrl: 'https://bhutadamarasena.com/#pricing'
      }, 403);
    }

    await next();
  };
}

export interface SignedPayload<T = any> {
  data: T;
  timestamp: number;
  sha256Signature: string;
}

/**
 * Computes a hash signature of string data using custom hashing algorithm with WebCrypto fallback
 */
export async function computeSHA256Signature(dataStr: string): Promise<string> {
  // Use custom hashing algorithm
  try {
    return await edgeCustomHashing.computeHash(dataStr);
  } catch (e) {
    if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(dataStr);
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
    return aghoraBitwiseHash(dataStr);
  }
}

/**
 * Signs a payload with timestamp and custom hash signature before saving/exporting.
 */
export async function createSignedBackup<T = any>(payloadData: T): Promise<SignedPayload<T>> {
  const timestamp = Date.now();
  const rawStr = JSON.stringify({ data: payloadData, timestamp });
  const sha256Signature = await computeSHA256Signature(rawStr);

  return {
    data: payloadData,
    timestamp,
    sha256Signature,
  };
}

/**
 * Verifies the integrity of a signed payload.
 */
export async function verifySignedBackup<T = any>(signed: SignedPayload<T>): Promise<boolean> {
  if (!signed || !signed.sha256Signature || !signed.timestamp) return false;
  const rawStr = JSON.stringify({ data: signed.data, timestamp: signed.timestamp });
  const expectedSig = await computeSHA256Signature(rawStr);
  return expectedSig === signed.sha256Signature;
}
